import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Activity,
  Save,
  RefreshCw,
  RotateCcw,
  Trash2,
  Plus,
  History,
  Key,
  Play,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  CalendarX,
  List as ListIcon,
  Search,
  X,
  Download,
  Clock,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";
import { apiGet, apiPost, apiDelete, API_BASE } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const SETTING_LABELS = {
  opening_range_minutes: "Opening range (minutes)",
  bar_size_minutes: "Bar size (minutes)",
  entry_cutoff_s5: "Setup 5 cut-off (HH:MM NY)",
  entry_cutoff_s6: "Setup 6 cut-off (HH:MM NY)",
  check_delay_seconds: "Check delay (seconds after bar close)",
  target_r: "Target R multiple",
  hold_days_s5: "Setup 5 max-hold (trading days)",
  hold_days_s6: "Setup 6 max-hold (trading days)",
  account_size: "Account size (USD per log)",
  risk_pct: "Risk % per trade",
  risk_pct_soxl: "Risk % per trade (SOXL)",
  cash_cap_pct: "Max cost per trade (% of equity)",
  max_positions: "Max concurrent positions",
  max_semis: "Max concurrent semiconductor positions",
  min_price: "Minimum entry price (USD)",
  sma_period: "SMA period for uptrend filter",
  check_times_nyc: "Scheduled check times (NY)",
  log_start_date: "Log start date (YYYY-MM-DD, blank = all)",
  scheduler_enabled: "Automatic scheduler (Stage 2 jobs)",
  enabled: "Module enabled",
};

const KEY_LABELS = {
  alpaca_key_id: "Alpaca Key ID",
  alpaca_secret: "Alpaca Secret",
  finnhub_api_key: "Finnhub API Key",
  telegram_bot_token: "Telegram Bot Token",
  telegram_chat_id: "Telegram Chat ID",
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export default function AdminOrbPage() {
  const [tab, setTab] = useState("settings");

  return (
    <div className="space-y-6" data-testid="admin-orb-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight flex items-center gap-3">
            <Activity className="h-7 w-7 text-primary" /> Oracle Trades
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
            US-stocks module running ORB V4 Setup 5 and Setup 6. Fully isolated from the
            CSE pipeline and Claude. Stage 1 ships the engine, data clients, admin
            settings and a deterministic <span className="font-mono">replay</span> engine —
            scheduler, trade log and Telegram alerts arrive in Stage 2.
          </p>
        </div>
        <Badge variant="secondary" className="text-xs">Stage 2 · Live &amp; Backfill</Badge>
      </header>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList className="grid grid-cols-7 w-full max-w-5xl" data-testid="orb-tabs">
          <TabsTrigger value="settings" data-testid="orb-tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="watchlist" data-testid="orb-tab-watchlist">Watchlist</TabsTrigger>
          <TabsTrigger value="skipdates" data-testid="orb-tab-skipdates">Skip Dates</TabsTrigger>
          <TabsTrigger value="earnings" data-testid="orb-tab-earnings">Earnings</TabsTrigger>
          <TabsTrigger value="keys" data-testid="orb-tab-keys">API Keys</TabsTrigger>
          <TabsTrigger value="replay" data-testid="orb-tab-replay">Replay</TabsTrigger>
          <TabsTrigger value="live" data-testid="orb-tab-live">Live Trades &amp; Jobs</TabsTrigger>
          <TabsTrigger value="backfill" data-testid="orb-tab-backfill">Backfill</TabsTrigger>
        </TabsList>

        <TabsContent value="settings"><SettingsPanel /></TabsContent>
        <TabsContent value="watchlist"><WatchlistPanel /></TabsContent>
        <TabsContent value="skipdates"><SkipDatesPanel /></TabsContent>
        <TabsContent value="earnings"><EarningsPanel /></TabsContent>
        <TabsContent value="keys"><KeysPanel /></TabsContent>
        <TabsContent value="replay"><ReplayPanel /></TabsContent>
        <TabsContent value="live"><LivePanel /></TabsContent>
        <TabsContent value="backfill"><BackfillPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ---------------------------- Settings panel ---------------------------- */
function SettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [defaults, setDefaults] = useState({});
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [period, setPeriod] = useState(null);
  const [newPeriodName, setNewPeriodName] = useState("");

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [s, h, lp] = await Promise.all([
        apiGet("/orb/settings"),
        apiGet("/orb/settings/history?limit=50"),
        apiGet("/orb/log-period"),
      ]);
      setDraft(s.settings);
      setDefaults(s.defaults);
      setHistory(h.history || []);
      setPeriod(lp.current);
    } catch (e) {
      toast.error("Failed to load ORB settings", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const dirtyKeys = useMemo(() => {
    return Object.keys(draft).filter((k) => JSON.stringify(draft[k]) !== JSON.stringify(defaults[k]));
  }, [draft, defaults]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...draft };
      if (Array.isArray(payload.check_times_nyc) && typeof payload.check_times_nyc[0] === "string") {
        // already string[]
      }
      const r = await apiPost("/orb/settings", payload);
      setDraft(r.settings);
      toast.success("ORB settings saved");
      fetchAll();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Reset ALL ORB settings to defaults?")) return;
    try {
      const r = await apiPost("/orb/settings/reset", {});
      setDraft(r.settings);
      toast.success("ORB settings reset to defaults");
      fetchAll();
    } catch (e) {
      toast.error("Reset failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const startNewPeriod = async () => {
    try {
      await apiPost("/orb/log-period/start", { name: newPeriodName || null });
      setNewPeriodName("");
      toast.success("New log period started");
      fetchAll();
    } catch (e) {
      toast.error("Could not start new period", { description: e?.response?.data?.detail || e.message });
    }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Strategy configuration</span>
            {dirtyKeys.length > 0 && (
              <Badge variant="secondary" data-testid="orb-settings-dirty-badge">{dirtyKeys.length} changed</Badge>
            )}
          </CardTitle>
          <CardDescription>
            Every change is validated server-side and recorded in the audit log.
            Changes apply from the next scheduled check — no restart required.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.keys(SETTING_LABELS).map((k) => (
              <SettingField
                key={k}
                k={k}
                value={draft[k]}
                defaultValue={defaults[k]}
                onChange={(v) => setDraft((d) => ({ ...d, [k]: v }))}
              />
            ))}
          </div>

          <div className="flex items-center gap-2 pt-2">
            <Button onClick={save} disabled={saving || dirtyKeys.length === 0} data-testid="orb-settings-save">
              <Save className="h-4 w-4 mr-2" /> Save
            </Button>
            <Button variant="outline" onClick={resetDefaults} data-testid="orb-settings-reset">
              <RotateCcw className="h-4 w-4 mr-2" /> Reset to defaults
            </Button>
            <Button variant="ghost" onClick={fetchAll} data-testid="orb-settings-refresh">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Log period</CardTitle>
          <CardDescription>
            Starting a new period closes current-period stats so results under
            new settings are reported separately. Old trades are kept forever.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <div className="font-medium" data-testid="orb-period-name">{period?.name || "—"}</div>
              <div className="text-muted-foreground">Started {fmtDateTime(period?.started_at)}</div>
            </div>
            <Badge variant="outline">Active</Badge>
          </div>
          <Separator />
          <div className="flex items-center gap-2">
            <Input
              placeholder="Optional label for the new period"
              value={newPeriodName}
              onChange={(e) => setNewPeriodName(e.target.value)}
              data-testid="orb-period-name-input"
            />
            <Button onClick={startNewPeriod} data-testid="orb-period-start">
              Start new log period
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><History className="h-4 w-4" /> Change history</CardTitle>
          <CardDescription>Last {history.length} settings changes.</CardDescription>
        </CardHeader>
        <CardContent>
          {history.length === 0 ? (
            <div className="text-sm text-muted-foreground">No changes recorded yet.</div>
          ) : (
            <div className="space-y-3">
              {history.map((h) => (
                <div key={h.id} className="rounded-md border p-3 text-sm" data-testid="orb-history-row">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{h.actor_email || "system"}</div>
                    <div className="text-xs text-muted-foreground">{fmtDateTime(h.at)}</div>
                  </div>
                  <ul className="mt-2 space-y-1 font-mono text-xs">
                    {Object.entries(h.diff || {}).map(([k, v]) => (
                      <li key={k}>
                        <span className="text-muted-foreground">{k}:</span>{" "}
                        <span className="line-through text-muted-foreground">{JSON.stringify(v.old)}</span>{" "}
                        → <span>{JSON.stringify(v.new)}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SettingField({ k, value, defaultValue, onChange }) {
  const label = SETTING_LABELS[k] || k;

  if (k === "enabled" || k === "scheduler_enabled") {
    return (
      <div className="flex items-center justify-between rounded-md border p-3" data-testid={`orb-setting-${k}`}>
        <div>
          <Label className="text-sm">{label}</Label>
          <div className="text-xs text-muted-foreground">Default: {String(defaultValue)}</div>
        </div>
        <Switch checked={!!value} onCheckedChange={onChange} data-testid={`orb-setting-${k}-switch`} />
      </div>
    );
  }

  if (k === "log_start_date") {
    return (
      <div className="space-y-1" data-testid={`orb-setting-${k}`}>
        <Label className="text-sm">{label}</Label>
        <Input type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
        <div className="text-xs text-muted-foreground">
          Trades started before this date are excluded from Live Trades &amp; summaries. Leave blank for all time.
        </div>
      </div>
    );
  }

  if (k === "check_times_nyc") {
    const text = Array.isArray(value) ? value.join(", ") : "";
    return (
      <div className="space-y-1" data-testid={`orb-setting-${k}`}>
        <Label className="text-sm">{label}</Label>
        <Input
          value={text}
          onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
          placeholder="10:00, 10:30, 11:00, 11:30, 12:00"
        />
        <div className="text-xs text-muted-foreground">Default: {(defaultValue || []).join(", ")}</div>
      </div>
    );
  }

  const isNumeric = typeof defaultValue === "number";
  return (
    <div className="space-y-1" data-testid={`orb-setting-${k}`}>
      <Label className="text-sm">{label}</Label>
      <Input
        type={isNumeric ? "number" : "text"}
        step={isNumeric ? "any" : undefined}
        value={value ?? ""}
        onChange={(e) => onChange(isNumeric ? Number(e.target.value) : e.target.value)}
      />
      <div className="text-xs text-muted-foreground">Default: {String(defaultValue)}</div>
    </div>
  );
}

/* ---------------------------- Watchlist panel --------------------------- */
function WatchlistPanel() {
  const [rows, setRows] = useState([]);
  const [newTicker, setNewTicker] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await apiGet("/orb/watchlist");
      setRows(r.watchlist || []);
    } catch (e) {
      toast.error("Could not load watchlist");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchRows(); }, []);

  const add = async () => {
    try {
      await apiPost("/orb/watchlist", { ticker: newTicker });
      setNewTicker("");
      toast.success("Added");
      fetchRows();
    } catch (e) {
      toast.error("Add failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Remove ${t}?`)) return;
    try {
      await apiDelete(`/orb/watchlist/${encodeURIComponent(t)}`);
      fetchRows();
    } catch (e) {
      toast.error("Remove failed");
    }
  };

  return (
    <Card data-testid="orb-watchlist-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><ListIcon className="h-4 w-4" /> Watchlist</CardTitle>
        <CardDescription>
          Admin-editable list of US tickers in <span className="font-mono">EXCHANGE:SYMBOL</span> form.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            value={newTicker}
            placeholder="NASDAQ:AMD"
            onChange={(e) => setNewTicker(e.target.value)}
            data-testid="orb-watchlist-input"
          />
          <Button onClick={add} data-testid="orb-watchlist-add">
            <Plus className="h-4 w-4 mr-2" /> Add
          </Button>
        </div>

        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
            {rows.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between rounded-md border p-2 text-sm"
                data-testid={`orb-watchlist-row-${r.ticker}`}
              >
                <div>
                  <div className="font-mono">{r.ticker}</div>
                  <div className="text-xs text-muted-foreground">
                    Added {fmtDateTime(r.added_at)}
                  </div>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  onClick={() => remove(r.ticker)}
                  data-testid={`orb-watchlist-remove-${r.ticker}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {rows.length === 0 && (
              <div className="col-span-full text-sm text-muted-foreground">No tickers yet.</div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------------------- Skip dates panel -------------------------- */
function SkipDatesPanel() {
  const [rows, setRows] = useState([]);
  const [newDate, setNewDate] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(true);

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await apiGet("/orb/skip-dates");
      setRows(r.skip_dates || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchRows(); }, []);

  const add = async () => {
    try {
      await apiPost("/orb/skip-dates", { date: newDate, reason });
      setNewDate("");
      setReason("");
      toast.success("Skip date added");
      fetchRows();
    } catch (e) {
      toast.error("Add failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove skip date ${d}?`)) return;
    await apiDelete(`/orb/skip-dates/${d}`);
    fetchRows();
  };

  return (
    <Card data-testid="orb-skipdates-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><CalendarX className="h-4 w-4" /> Skip dates</CardTitle>
        <CardDescription>
          FOMC statement days, US half-days or any date you want to exclude.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-2">
          <Input
            type="date"
            value={newDate}
            onChange={(e) => setNewDate(e.target.value)}
            data-testid="orb-skipdate-input"
          />
          <Input
            placeholder="Reason (e.g. FOMC)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="orb-skipdate-reason"
          />
          <Button onClick={add} data-testid="orb-skipdate-add">
            <Plus className="h-4 w-4 mr-2" /> Add
          </Button>
        </div>
        {loading ? (
          <Skeleton className="h-40 w-full" />
        ) : (
          <div className="space-y-2">
            {rows.map((r) => (
              <div
                key={r.id}
                className="flex items-center justify-between rounded-md border p-2 text-sm"
                data-testid={`orb-skipdate-row-${r.date}`}
              >
                <div>
                  <div className="font-mono">{r.date}</div>
                  <div className="text-xs text-muted-foreground">{r.reason || "—"}</div>
                </div>
                <Button size="icon" variant="ghost" onClick={() => remove(r.date)}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {rows.length === 0 && <div className="text-sm text-muted-foreground">No skip dates.</div>}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ---------------------------- Keys panel -------------------------------- */
function ManualEarningsEditor() {
  const [items, setItems] = useState([]);
  const [sym, setSym] = useState("");
  const [dte, setDte] = useState("");
  const [hour, setHour] = useState("amc");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await apiGet("/orb/earnings/manual");
      setItems(r.items || []);
    } catch (e) { /* non-fatal */ }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!sym || !dte) return toast.error("Symbol and date are required");
    setBusy(true);
    try {
      await apiPost("/orb/earnings/manual", { symbol: sym.toUpperCase(), date: dte, hour, note });
      toast.success(`Added ${sym} ${dte} ${hour}`);
      setSym(""); setDte(""); setNote("");
      await load();
    } catch (e) { toast.error(e.message || "Add failed"); }
    finally { setBusy(false); }
  };
  const del = async (s, d) => {
    try {
      await apiDelete(`/orb/earnings/manual/${encodeURIComponent(s)}/${encodeURIComponent(d)}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error(e.message || "Delete failed"); }
  };

  return (
    <Card className="mt-4" data-testid="orb-manual-earnings">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm">Manual earnings dates (override)</CardTitle>
        <CardDescription className="text-xs">
          Admin-maintained earnings dates. Manual entries override fetched ones for the same symbol+date.
          Use this when yfinance/Finnhub miss an announcement (e.g. COKE 2023–2025).
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap gap-2 items-end">
          <div><div className="text-[11px] text-muted-foreground">Symbol</div>
            <Input value={sym} onChange={e => setSym(e.target.value.toUpperCase())} placeholder="COKE" className="w-28" data-testid="manual-earn-sym" /></div>
          <div><div className="text-[11px] text-muted-foreground">Date</div>
            <Input type="date" value={dte} onChange={e => setDte(e.target.value)} className="w-36" data-testid="manual-earn-date" /></div>
          <div><div className="text-[11px] text-muted-foreground">Hour</div>
            <select className="h-9 border rounded px-2 text-sm bg-background" value={hour} onChange={e => setHour(e.target.value)} data-testid="manual-earn-hour">
              <option value="amc">amc</option><option value="bmo">bmo</option><option value="unknown">unknown</option>
            </select></div>
          <div className="flex-1 min-w-[150px]"><div className="text-[11px] text-muted-foreground">Note</div>
            <Input value={note} onChange={e => setNote(e.target.value)} placeholder="optional" data-testid="manual-earn-note" /></div>
          <Button onClick={add} disabled={busy} data-testid="manual-earn-add">Add / Update</Button>
        </div>
        {items.length === 0 ? (
          <div className="text-xs text-muted-foreground">No manual overrides yet.</div>
        ) : (
          <div className="rounded border divide-y max-h-72 overflow-auto">
            {items.map((it, i) => (
              <div key={i} className="p-2 flex items-center gap-3 text-xs font-mono">
                <span className="font-semibold">{it.symbol}</span>
                <span>{it.date}</span>
                <Badge variant="outline" className="text-[10px] uppercase">{it.hour}</Badge>
                {it.note && <span className="opacity-70 italic">{it.note}</span>}
                <Button variant="ghost" size="sm" className="ml-auto h-6" onClick={() => del(it.symbol, it.date)}>Delete</Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EarningsPanel() {
  const [cache, setCache] = useState([]);
  const [watchlistRows, setWatchlistRows] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const loadCache = async () => {
    setLoading(true); setError("");
    try {
      const r = await apiGet("/orb/earnings/cache");
      setCache(r.cache || []);
    } catch (e) {
      setError(e.message || "Failed to load cache");
    } finally {
      setLoading(false);
    }
  };

  const loadWatchlist = async () => {
    try {
      const r = await apiGet("/orb/watchlist");
      setWatchlistRows(r.items || r.watchlist || r || []);
    } catch (e) {
      // non-fatal
    }
  };

  useEffect(() => { loadCache(); loadWatchlist(); }, []);

  const refresh = async () => {
    setRefreshing(true); setError("");
    try {
      const r = await apiPost("/orb/earnings/refresh", {});
      setReport(r);
      await loadCache();
      toast.success(
        r.has_warnings
          ? "Earnings refreshed (with warnings — see red banner)"
          : "Earnings refreshed for all symbols"
      );
    } catch (e) {
      setError(e.message || "Refresh failed");
      toast.error("Earnings refresh failed");
    } finally {
      setRefreshing(false);
    }
  };

  const flatSymbols = (() => {
    const list = (() => {
      if (report && report.symbols) {
        return Object.entries(report.symbols).map(([sym, info]) => ({
          symbol: sym,
          source: info.source,
          events: info.events || [],
          warnings: info.warnings || [],
        }));
      }
      return (cache || []).map((d) => ({
        symbol: d.symbol,
        source: d.source,
        events: (d.rows || []).map((r) => ({
          date: r.date, hour: r.hour, raw_hour: r.raw_hour,
        })),
        warnings: [],
        fetched_at: d.fetched_at,
      }));
    })();
    // Attach the no_earnings flag from the watchlist so the UI knows
    // which rows should render as grey "ETF" instead of red "no data".
    const flagMap = new Map();
    for (const w of watchlistRows || []) {
      const bareSym = (w.ticker || "").split(":").pop();
      if (bareSym) flagMap.set(bareSym, { ticker: w.ticker, no_earnings: !!w.no_earnings });
    }
    return list.map((s) => ({
      ...s,
      ticker: (flagMap.get(s.symbol) || {}).ticker || null,
      no_earnings: (flagMap.get(s.symbol) || {}).no_earnings || s.source === "etf",
    }));
  })();

  const anyEmpty = flatSymbols.some((s) => (s.events || []).length === 0 && !s.no_earnings);

  const toggleNoEarnings = async (ticker, nextFlag) => {
    if (!ticker) return;
    try {
      await apiPost(`/orb/watchlist/${encodeURIComponent(ticker)}/no-earnings`, { flag: nextFlag });
      toast.success(nextFlag ? `${ticker} flagged as ETF / no earnings` : `${ticker} un-flagged`);
      await loadCache();
      await loadWatchlist();
    } catch (e) {
      toast.error(`Toggle failed: ${e.message || e}`);
    }
  };

  return (
    <Card data-testid="orb-earnings-panel">
      <CardHeader>
        <CardTitle>Earnings calendar</CardTitle>
        <CardDescription>
          Finnhub is primary; yfinance is the fallback. Cached in Mongo for 24 h. If a symbol loads zero earnings dates, entries will NOT be blocked — the symbol shows a red warning here, on every replay result, and (in live mode) a one-shot Telegram alert per day.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex gap-2">
          <Button onClick={refresh} disabled={refreshing} data-testid="orb-earnings-refresh-btn">
            {refreshing ? "Refreshing…" : "Refresh earnings dates"}
          </Button>
          <Button variant="outline" onClick={loadCache} disabled={loading} data-testid="orb-earnings-reload-btn">
            {loading ? "Loading…" : "Reload cache"}
          </Button>
        </div>

        <ManualEarningsEditor />

        {error && (
          <Alert variant="destructive" data-testid="orb-earnings-panel-error">
            <AlertDescription className="text-xs font-mono">{error}</AlertDescription>
          </Alert>
        )}

        {report && report.has_warnings && (
          <Alert variant="destructive" data-testid="orb-earnings-panel-warning">
            <AlertTitle className="text-sm">One or more symbols failed to load earnings</AlertTitle>
            <AlertDescription className="text-xs">
              Scroll to the red-badged symbols below. Check backend logs for <span className="font-mono">orb.finnhub</span> / <span className="font-mono">orb.earnings_fetcher</span> entries.
            </AlertDescription>
          </Alert>
        )}
        {!report && anyEmpty && cache.length > 0 && (
          <Alert variant="destructive">
            <AlertDescription className="text-xs">Some cached symbols have zero earnings — hit "Refresh earnings dates".</AlertDescription>
          </Alert>
        )}

        {flatSymbols.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No earnings cache yet. Click "Refresh earnings dates" to populate.
          </div>
        ) : (
          <div className="rounded-md border divide-y max-h-[560px] overflow-auto">
            {flatSymbols.map((s) => (
              <div key={s.symbol} className={`p-3 space-y-1 ${s.no_earnings ? "bg-muted/40" : ""}`} data-testid={`orb-earnings-row-${s.symbol}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono font-semibold text-sm">{s.symbol}</span>
                  {s.no_earnings ? (
                    <Badge
                      variant="outline"
                      className="text-[10px] uppercase bg-muted"
                      data-testid={`orb-earnings-etf-badge-${s.symbol}`}
                    >
                      ETF – no earnings
                    </Badge>
                  ) : (
                    <Badge
                      variant={(s.events || []).length === 0 ? "destructive" : (s.source === "finnhub" ? "default" : "secondary")}
                      className="text-[10px] uppercase"
                    >
                      {s.source || "unknown"}
                    </Badge>
                  )}
                  <span className="text-[11px] text-muted-foreground">
                    {s.no_earnings ? "—" : `${(s.events || []).length} dates`}
                  </span>
                  {s.fetched_at && (
                    <span className="text-[11px] text-muted-foreground font-mono">
                      cached: {s.fetched_at.slice(0, 19).replace("T", " ")}
                    </span>
                  )}
                  {s.ticker && (
                    <label className="ml-auto flex items-center gap-1 text-[11px] text-muted-foreground">
                      <input
                        type="checkbox"
                        checked={!!s.no_earnings}
                        onChange={(e) => toggleNoEarnings(s.ticker, e.target.checked)}
                        data-testid={`orb-earnings-noflag-toggle-${s.symbol}`}
                      />
                      ETF / no earnings
                    </label>
                  )}
                </div>
                {!s.no_earnings && (s.warnings || []).length > 0 && (
                  <div className="text-[11px] text-destructive font-mono space-y-0.5 pl-3">
                    {s.warnings.map((w, i) => <div key={i}>{w}</div>)}
                  </div>
                )}
                {s.no_earnings ? (
                  <div className="text-[12px] text-muted-foreground pl-3">
                    This symbol has no earnings (ETF / fund). Entries are never blocked for earnings.
                  </div>
                ) : (s.events || []).length === 0 ? (
                  <div className="text-[12px] text-destructive font-semibold pl-3">
                    NO EARNINGS LOADED — entries will not be blocked for this symbol.
                  </div>
                ) : (
                  <div className="pl-3 text-xs grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-0.5">
                    {s.events.map((ev, i) => (
                      <div key={i} className="flex gap-2">
                        <span className="font-mono">{ev.date}</span>
                        <Badge variant="outline" className="text-[10px] uppercase">
                          {ev.hour || "unknown"}
                        </Badge>
                        {ev.raw_hour && ev.raw_hour !== ev.hour && (
                          <span className="text-[10px] text-muted-foreground">raw: {ev.raw_hour}</span>
                        )}
                        {ev.blocked_dates && ev.blocked_dates.length > 0 && (
                          <span className="text-muted-foreground">
                            blocks <span className="font-mono">{ev.blocked_dates.join(", ")}</span>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


function KeysPanel() {
  const [rows, setRows] = useState([]);
  const [masterKey, setMasterKey] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState({});

  const fetchRows = async () => {
    setLoading(true);
    try {
      const r = await apiGet("/orb/keys");
      setRows(r.keys || []);
      setMasterKey(r.master_key || null);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchRows(); }, []);

  const save = async (slot) => {
    try {
      await apiPost("/orb/keys", { slot, value: values[slot] || "" });
      setValues((v) => ({ ...v, [slot]: "" }));
      toast.success(`${KEY_LABELS[slot]} saved`);
      fetchRows();
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  const remove = async (slot) => {
    if (!window.confirm(`Delete ${KEY_LABELS[slot]}?`)) return;
    await apiDelete(`/orb/keys/${slot}`);
    fetchRows();
  };

  const testProvider = async (provider) => {
    setTesting((t) => ({ ...t, [provider]: true }));
    try {
      const r = await apiPost("/orb/keys/test", { provider });
      if (r.ok) toast.success(`${provider} connection OK: ${r.message}`);
      else toast.error(`${provider} connection failed`, { description: r.message });
      fetchRows();
    } catch (e) {
      toast.error("Test failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setTesting((t) => ({ ...t, [provider]: false }));
    }
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  const mk = masterKey || {};
  const sourceLabel = {
    env: "Environment variable (ORB_SECRETS_KEY)",
    file: "Secrets key file",
    "file-created": "Secrets key file (auto-generated on first start)",
    "mongo-migrated": "Secrets key file (migrated from legacy DB fallback)",
    ephemeral: "Ephemeral (in-memory) — NOT persisted!",
    missing: "Not set",
  }[mk.source] || "Unknown";

  return (
    <div className="space-y-4">
      <Alert variant={mk.set ? "default" : "destructive"} data-testid="orb-master-key-status">
        <Key className="h-4 w-4" />
        <AlertTitle>
          Master encryption key:{" "}
          {mk.set ? (
            <Badge variant="secondary" className="ml-1 bg-emerald-50 text-emerald-700 border border-emerald-200">
              Set
            </Badge>
          ) : (
            <Badge variant="destructive" className="ml-1">Not set</Badge>
          )}
        </AlertTitle>
        <AlertDescription>
          <div className="mt-2 space-y-1 text-sm">
            <div><span className="text-muted-foreground">Source:</span> {sourceLabel}</div>
            {mk.file_path && (
              <div>
                <span className="text-muted-foreground">File:</span>{" "}
                <span className="font-mono">{mk.file_path}</span>{" "}
                {mk.file_present ? (
                  <Badge variant="outline" className="ml-1">present {mk.file_mode ? `(${mk.file_mode})` : ""}</Badge>
                ) : (
                  <Badge variant="outline" className="ml-1">missing</Badge>
                )}
              </div>
            )}
            <div className="text-xs text-muted-foreground">
              Secrets are Fernet-encrypted. The master key is never stored in MongoDB,
              never sent to the browser, and never returned by the API.
              Back up the key file (same contents = same key) — losing it makes stored
              secrets unrecoverable.
            </div>
          </div>
        </AlertDescription>
      </Alert>

      <Card data-testid="orb-keys-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Key className="h-4 w-4" /> API Keys</CardTitle>
          <CardDescription>
            Needed by Stage 1 (Alpaca + Finnhub for replay) and Stage 2 (Telegram for alerts).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {rows.map((row) => (
            <div key={row.slot} className="space-y-2" data-testid={`orb-key-${row.slot}`}>
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">{KEY_LABELS[row.slot] || row.slot}</Label>
                <div className="flex items-center gap-2">
                  {row.present ? (
                    <Badge variant="secondary" className="font-mono">{row.preview || "set"}</Badge>
                  ) : (
                    <Badge variant="outline">Not set</Badge>
                  )}
                  {row.last_test ? (
                    row.last_test.ok ? (
                      <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <CheckCircle2 className="h-3 w-3 mr-1" /> OK
                      </Badge>
                    ) : (
                      <Badge variant="destructive">
                        <XCircle className="h-3 w-3 mr-1" /> Failed
                      </Badge>
                    )
                  ) : null}
                </div>
              </div>
              <div className="flex gap-2">
                <Input
                  type="password"
                  placeholder={row.present ? "Replace value" : "Enter value"}
                  value={values[row.slot] || ""}
                  onChange={(e) => setValues((v) => ({ ...v, [row.slot]: e.target.value }))}
                  data-testid={`orb-key-input-${row.slot}`}
                />
                <Button onClick={() => save(row.slot)} data-testid={`orb-key-save-${row.slot}`}>
                  Save
                </Button>
                {row.present && (
                  <Button variant="outline" onClick={() => remove(row.slot)} data-testid={`orb-key-remove-${row.slot}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              {row.updated_at && (
                <div className="text-xs text-muted-foreground">Updated {fmtDateTime(row.updated_at)}</div>
              )}
            </div>
          ))}

          <Separator />
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => testProvider("alpaca")}
              disabled={!!testing.alpaca}
              data-testid="orb-key-test-alpaca"
            >
              <Play className="h-4 w-4 mr-2" /> Test Alpaca
            </Button>
            <Button
              variant="outline"
              onClick={() => testProvider("finnhub")}
              disabled={!!testing.finnhub}
              data-testid="orb-key-test-finnhub"
            >
              <Play className="h-4 w-4 mr-2" /> Test Finnhub
            </Button>
            <Button
              variant="outline"
              onClick={() => testProvider("telegram")}
              disabled={!!testing.telegram}
              data-testid="orb-key-test-telegram"
            >
              <Play className="h-4 w-4 mr-2" /> Test Telegram
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Test Telegram actually delivers a real one-line probe message to the configured chat
            — if you don't receive it in Telegram, either the bot token or the chat ID is wrong.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

/* ---------------------------- Replay panel ------------------------------ */
const QUICK_RANGES = [
  { key: "1m", label: "Last 1 month", months: 1 },
  { key: "3m", label: "Last 3 months", months: 3 },
  { key: "6m", label: "Last 6 months", months: 6 },
  { key: "ytd", label: "Year to date", ytd: true },
];

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function minusMonths(iso, months) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCMonth(dt.getUTCMonth() - months);
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

function diffDays(a, b) {
  const [ay, am, ad] = a.split("-").map(Number);
  const [by, bm, bd] = b.split("-").map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / (1000 * 60 * 60 * 24));
}

function money(x) {
  if (x === null || x === undefined || isNaN(x)) return "—";
  const n = Number(x);
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function r2(x) {
  if (x === null || x === undefined || isNaN(x)) return "—";
  return Number(x).toFixed(2);
}
function pnlClass(x) {
  if (x === null || x === undefined || isNaN(x)) return "text-muted-foreground";
  if (Number(x) > 0) return "text-emerald-600 dark:text-emerald-400 font-medium";
  if (Number(x) < 0) return "text-red-600 dark:text-red-400 font-medium";
  return "text-muted-foreground";
}
function exitBadge(reason, pnl) {
  if (reason === "Target") return <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">Target</Badge>;
  if (reason === "Stop") return <Badge className="bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">Stop</Badge>;
  if (reason === "Max hold") {
    const cls = Number(pnl || 0) >= 0
      ? "bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800"
      : "bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800";
    return <Badge className={cls}>Max hold</Badge>;
  }
  return <Badge variant="outline">{reason || "—"}</Badge>;
}

function StockPicker({ selected, onChange, allWatchlist, onAllWatchlistChange }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ pinned: [], universe: [], watchlist_tickers: [] });
  const [query, setQuery] = useState("");

  useEffect(() => {
    apiGet("/orb/stocks").then(setData).catch(() => {
      toast.error("Could not load stock list");
    });
  }, []);

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const toggle = (ticker) => {
    const n = new Set(selected);
    if (n.has(ticker)) n.delete(ticker); else n.add(ticker);
    onChange(Array.from(n));
  };

  const filtered = useMemo(() => {
    const q = (query || "").trim().toUpperCase();
    const match = (s) => !q || (s.ticker || "").toUpperCase().includes(q) || (s.label || "").toUpperCase().includes(q);
    return {
      pinned: (data.pinned || []).filter(match),
      universe: (data.universe || []).filter(match).slice(0, 150),
    };
  }, [data, query]);

  return (
    <div className="space-y-2" data-testid="orb-stock-picker">
      <div className="flex items-center justify-between">
        <Label className="text-sm">Stocks</Label>
        <div className="flex items-center gap-2 text-xs">
          <Checkbox
            id="all-watchlist"
            checked={!!allWatchlist}
            onCheckedChange={(v) => onAllWatchlistChange(!!v)}
            data-testid="orb-all-watchlist"
          />
          <Label htmlFor="all-watchlist" className="text-xs cursor-pointer">All watchlist stocks</Label>
        </div>
      </div>

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" className="w-full justify-between" data-testid="orb-stock-picker-trigger">
            <span className="flex items-center gap-2 truncate">
              <Search className="h-4 w-4" />
              {allWatchlist
                ? "All 17 watchlist stocks"
                : (selected.length === 0 ? "Pick stocks…" : `${selected.length} selected`)}
            </span>
            {selected.length > 0 && !allWatchlist && (
              <X
                className="h-4 w-4 opacity-60 hover:opacity-100"
                onClick={(e) => { e.stopPropagation(); onChange([]); }}
              />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[480px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput placeholder="Search ticker or name…" value={query} onValueChange={setQuery} />
            <CommandList className="max-h-[380px]">
              <CommandEmpty>No matches.</CommandEmpty>
              {filtered.pinned.length > 0 && (
                <CommandGroup heading="Watchlist (ORB defaults)">
                  {filtered.pinned.map((s) => (
                    <CommandItem
                      key={s.ticker}
                      value={s.ticker}
                      onSelect={() => toggle(s.ticker)}
                      data-testid={`orb-pick-${s.ticker}`}
                    >
                      <Checkbox checked={selectedSet.has(s.ticker)} className="mr-2" />
                      <span className="font-mono mr-2">{s.symbol}</span>
                      <span className="text-xs text-muted-foreground">{s.ticker}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {filtered.universe.length > 0 && <CommandSeparator />}
              {filtered.universe.length > 0 && (
                <CommandGroup heading={`US Universe (${filtered.universe.length}${(data.universe || []).length > 150 ? "+" : ""})`}>
                  {filtered.universe.map((s) => (
                    <CommandItem
                      key={s.ticker}
                      value={s.ticker}
                      onSelect={() => toggle(s.ticker)}
                      data-testid={`orb-pick-${s.ticker}`}
                    >
                      <Checkbox checked={selectedSet.has(s.ticker)} className="mr-2" />
                      <span className="font-mono mr-2">{s.symbol}</span>
                      <span className="text-xs text-muted-foreground truncate">{s.label}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selected.length > 0 && !allWatchlist && (
        <div className="flex flex-wrap gap-1" data-testid="orb-selected-chips">
          {selected.map((t) => (
            <Badge key={t} variant="secondary" className="font-mono text-xs">
              {t}
              <X className="h-3 w-3 ml-1 cursor-pointer" onClick={() => toggle(t)} />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}

function SingleDayReplay() {
  const [symbol, setSymbol] = useState("NASDAQ:AMD");
  const [date, setDate] = useState("");
  const [feed, setFeed] = useState("sip");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!symbol || !date) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await apiPost("/orb/replay", { symbol, date, feed });
      setResult(r);
    } catch (e) {
      toast.error("Replay failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="orb-single-day-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Clock className="h-4 w-4" /> Single-day replay</CardTitle>
        <CardDescription>
          Fast sanity check of the engine against TradingView for one day.
          Historical replays use Alpaca's SIP feed by default (consolidated tape, matches
          TradingView); switch to IEX only to reproduce what the live scheduler would see.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
          <Input placeholder="NASDAQ:AMD" value={symbol} onChange={(e) => setSymbol(e.target.value)} data-testid="orb-replay-symbol" />
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="orb-replay-date" />
          <div className="flex items-center gap-2 border rounded-md px-2" data-testid="orb-replay-feed">
            <Label className="text-xs text-muted-foreground">Feed</Label>
            <div className="flex gap-1 py-1">
              {["sip", "iex"].map((f) => (
                <Button
                  key={f}
                  size="sm"
                  variant={feed === f ? "default" : "outline"}
                  onClick={() => setFeed(f)}
                  data-testid={`orb-replay-feed-${f}`}
                >
                  {f.toUpperCase()}
                </Button>
              ))}
            </div>
          </div>
          <Button onClick={run} disabled={loading} data-testid="orb-replay-run">
            {loading ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            Run
          </Button>
        </div>

        {result && result.non_trading_day && (
          <Alert data-testid="orb-nontrading-alert">
            <CalendarX className="h-4 w-4" />
            <AlertTitle>{result.reason}</AlertTitle>
            <AlertDescription>Pick a US trading day (Mon–Fri, excluding US market holidays and admin skip dates).</AlertDescription>
          </Alert>
        )}
        {result && result.no_data && (
          <Alert data-testid="orb-nodata-alert">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>No data from Alpaca</AlertTitle>
            <AlertDescription>Alpaca returned no 30-minute bars for this symbol on {result.day}.</AlertDescription>
          </Alert>
        )}

        {result && !result.non_trading_day && !result.no_data && (
          <div className="space-y-3">
            {result.errors?.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Partial result</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc pl-5">{result.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </AlertDescription>
              </Alert>
            )}
            <div className="rounded-md border p-3 text-sm">
              <div className="flex items-center justify-between">
                <div className="font-mono">{result.symbol} · {result.day}</div>
                <Badge variant="outline" className="font-mono text-xs" data-testid="orb-feed-badge">
                  Feed: {(result.feed_used || result.feed || "sip").toUpperCase()}
                </Badge>
              </div>
              <div className="mt-2">
                Opening range: <span className="font-mono">
                  high {r2(result.opening_range?.high)} · low {r2(result.opening_range?.low)}
                </span>
              </div>
            </div>

            {result.debug && (
              <Card data-testid="orb-debug-card" className="border-dashed">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Debug payload</CardTitle>
                  <CardDescription className="text-xs">
                    30-min bars 10:00–12:00 NY, SMA200 context, earnings flag — use this to reconcile against TradingView.
                  </CardDescription>
                </CardHeader>
                <CardContent className="text-xs space-y-2">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 font-mono">
                    <div>Prev daily close: <b>{r2(result.debug.prev_daily_close)}</b></div>
                    <div>SMA200: <b>{r2(result.debug.sma_200)}</b></div>
                    <div>Above SMA200: <b>{String(result.debug.sma_check?.above_sma ?? "n/a")}</b></div>
                    <div>Earnings today: <b>{String(result.debug.earnings_today)}</b></div>
                    <div>Day-after earnings: <b>{String(result.debug.day_after_earnings)}</b></div>
                    <div>Skip date: <b>{String(result.debug.is_skip_date)}</b></div>
                  </div>
                  {Array.isArray(result.debug.bars_10_to_12_ny) && result.debug.bars_10_to_12_ny.length > 0 && (
                    <div className="pt-2">
                      <div className="text-muted-foreground mb-1">30-min bars (NY open time, OHLC):</div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs font-mono">
                          <thead>
                            <tr className="text-muted-foreground">
                              <th className="text-left pr-3">Open NY</th>
                              <th className="text-right pr-3">O</th>
                              <th className="text-right pr-3">H</th>
                              <th className="text-right pr-3">L</th>
                              <th className="text-right">C</th>
                            </tr>
                          </thead>
                          <tbody>
                            {result.debug.bars_10_to_12_ny.map((b, i) => (
                              <tr key={i}>
                                <td className="pr-3">{b.open_time_ny}</td>
                                <td className="text-right pr-3">{b.o}</td>
                                <td className="text-right pr-3">{b.h}</td>
                                <td className="text-right pr-3">{b.l}</td>
                                <td className="text-right">{b.c}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {["setup_5", "setup_6"].map((k) => (
              <SetupResultCard key={k} name={k === "setup_5" ? "Setup 5" : "Setup 6"} data={result.setups?.[k]} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function SetupResultCard({ name, data }) {
  if (!data) return null;
  const { filters, signal, exit, sizing } = data;
  const pnl = sizing && exit && exit.price !== null && exit.price !== undefined
    ? (exit.price - signal.entry) * (sizing.shares || 0)
    : null;
  const rResult = signal && exit && exit.price !== null && exit.price !== undefined && signal.risk_per_share
    ? (exit.price - signal.entry) / signal.risk_per_share
    : null;
  const signalCloseNy = signal ? formatBarClose(signal.signal_bar_open, 30) : null;
  return (
    <Card data-testid={`orb-single-day-${name.toLowerCase().replace(/\s+/g, "-")}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          {name}
          {signal
            ? (filters?.passes
              ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">Would enter</Badge>
              : <Badge variant="outline">Signal (filtered out)</Badge>)
            : <Badge variant="secondary">No signal</Badge>}
        </CardTitle>
      </CardHeader>
      <CardContent className="text-sm space-y-2">
        <div className="font-mono">
          <span className="text-muted-foreground">Filters:</span>{" "}
          {filters?.passes ? "pass" : (filters?.reasons || []).join(", ") || "—"}
        </div>
        {signal && (
          <div className="font-mono">
            <span className="text-muted-foreground">Signal:</span>{" "}
            close {signalCloseNy?.ny} NY / {signalCloseNy?.uk} UK (bar open {signalCloseNy?.openNy})
            {" · "}entry {r2(signal.entry)} · stop {r2(signal.stop)} · target {r2(signal.target)}
            {signal.prev_bar_close !== undefined && signal.prev_bar_close !== null && (
              <span className="text-muted-foreground"> · prev bar close {r2(signal.prev_bar_close)}</span>
            )}
          </div>
        )}
        {exit && (
          <div className="font-mono">
            <span className="text-muted-foreground">Exit:</span>{" "}
            {exitBadge(exit.reason, pnl)}{" "}
            at {r2(exit.price)} {exit.time ? `(${exit.time})` : ""}
            {exit.gap_fill && <Badge variant="outline" className="ml-2 text-amber-600 border-amber-200">gap-fill</Badge>}
            {exit.earnings_warning && <span className="ml-2 text-amber-600">· earnings during hold</span>}
          </div>
        )}
        {sizing && (
          <div className="font-mono">
            <span className="text-muted-foreground">Sizing:</span>{" "}
            {sizing.shares} shares · cost {money(sizing.trade_cost)} · fits_limits {String(sizing.fits_limits)}
          </div>
        )}
        {pnl !== null && (
          <div className="font-mono">
            <span className="text-muted-foreground">P/L:</span>{" "}
            <span className={pnlClass(pnl)}>{money(pnl)}</span>{" · "}
            <span className={pnlClass(rResult)}>{r2(rResult)}R</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function formatBarClose(isoOpen, barMinutes) {
  if (!isoOpen) return null;
  const d = new Date(isoOpen);
  const close = new Date(d.getTime() + barMinutes * 60 * 1000);
  const ny = close.toLocaleTimeString("en-GB", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false });
  const uk = close.toLocaleTimeString("en-GB", { timeZone: "Europe/London", hour: "2-digit", minute: "2-digit", hour12: false });
  const openNy = d.toLocaleTimeString("en-GB", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hour12: false });
  return { ny, uk, openNy };
}

function RangeReplay() {
  const [selected, setSelected] = useState([]);
  const [allWatchlist, setAllWatchlist] = useState(false);
  const [start, setStart] = useState(minusMonths(todayISO(), 1));
  const [end, setEnd] = useState(todayISO());
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const applyQuick = (opt) => {
    const to = todayISO();
    let from = minusMonths(to, opt.months || 1);
    if (opt.ytd) from = `${new Date().getFullYear()}-01-01`;
    setStart(from);
    setEnd(to);
  };

  const rangeDays = useMemo(() => diffDays(start, end), [start, end]);
  const rangeInvalid = rangeDays < 0 || rangeDays > 366;

  const start_ = async () => {
    setResult(null);
    setError(null);
    setStatus(null);
    try {
      const r = await apiPost("/orb/replay-range", {
        tickers: allWatchlist ? [] : selected,
        all_watchlist: allWatchlist,
        start,
        end,
      });
      setJobId(r.job_id);
    } catch (e) {
      toast.error("Could not start replay", { description: e?.response?.data?.detail || e.message });
    }
  };

  useEffect(() => {
    if (!jobId) return;
    const tick = async () => {
      try {
        const r = await apiGet(`/orb/replay-range/jobs/${jobId}`);
        setStatus(r);
        if (r.status === "completed") {
          setResult(r.result || null);
          clearInterval(pollRef.current);
          pollRef.current = null;
        } else if (r.status === "failed") {
          setError(r.error || "replay failed");
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    tick();
    pollRef.current = setInterval(tick, 1500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobId]);

  const progress = status?.progress || null;
  const pct = progress && progress.total ? Math.min(100, Math.round((progress.current / progress.total) * 100)) : 0;

  const downloadCsv = async (setup) => {
    try {
      const resp = await (await import("@/lib/api")).api.get(
        `/orb/replay-range/jobs/${jobId}/csv/${setup}`, { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `oracle_trades_setup_${setup}_${jobId.slice(0, 8)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("CSV download failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <Card data-testid="orb-range-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Play className="h-4 w-4" /> Date-range replay</CardTitle>
        <CardDescription>
          Walks the engine day-by-day across the range, applying live-trading rules
          (one open trade per stock per setup, exits by stop / target / max hold, sizing on
          each setup's own virtual equity starting at the admin account size).
          Weekends and US market holidays are skipped automatically.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <StockPicker
          selected={selected}
          onChange={setSelected}
          allWatchlist={allWatchlist}
          onAllWatchlistChange={setAllWatchlist}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-sm">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="orb-range-start" />
          </div>
          <div className="space-y-1">
            <Label className="text-sm">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="orb-range-end" />
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {QUICK_RANGES.map((q) => (
            <Button
              key={q.key}
              size="sm"
              variant="outline"
              onClick={() => applyQuick(q)}
              data-testid={`orb-quick-${q.key}`}
            >
              {q.label}
            </Button>
          ))}
          <span className="text-xs text-muted-foreground self-center ml-1">
            {rangeInvalid ? "Invalid range (max 12 months)" : `${rangeDays} days selected`}
          </span>
        </div>

        <div>
          <Button
            onClick={start_}
            disabled={rangeInvalid || (!allWatchlist && selected.length === 0) || (status && status.status !== "completed" && status.status !== "failed" && jobId)}
            data-testid="orb-range-run"
          >
            <Play className="h-4 w-4 mr-2" /> Run range replay
          </Button>
        </div>

        {status && status.status !== "completed" && (
          <div className="space-y-2" data-testid="orb-range-progress">
            <div className="flex items-center justify-between text-sm">
              <span>{progress?.message || status.status}</span>
              <span className="text-muted-foreground">{progress?.current ?? 0} / {progress?.total ?? 0}</span>
            </div>
            <Progress value={pct} />
          </div>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Replay failed</AlertTitle>
            <AlertDescription>{String(error)}</AlertDescription>
          </Alert>
        )}

        {result && (
          <RangeResults result={result} jobId={jobId} onDownloadCsv={downloadCsv} />
        )}
      </CardContent>
    </Card>
  );
}

function RangeResults({ result, jobId, onDownloadCsv }) {
  const setups = ["setup_5", "setup_6"];
  const [tab, setTab] = useState("setup_5");

  return (
    <div className="space-y-4" data-testid="orb-range-results">
      <div className="flex items-center justify-end">
        <Badge variant="outline" className="font-mono text-xs" data-testid="orb-range-feed-badge">
          Feed: {(result.feed || "sip").toUpperCase()}
        </Badge>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="setup_5" data-testid="orb-results-tab-s5">Setup 5</TabsTrigger>
          <TabsTrigger value="setup_6" data-testid="orb-results-tab-s6">Setup 6</TabsTrigger>
        </TabsList>
        {setups.map((k) => (
          <TabsContent key={k} value={k} className="space-y-4">
            <SetupResultsPanel
              data={result.setups?.[k]}
              setupNumber={k === "setup_5" ? 5 : 6}
              onDownloadCsv={() => onDownloadCsv(k === "setup_5" ? "5" : "6")}
            />
          </TabsContent>
        ))}
      </Tabs>
      {(result.skipped || []).length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Skipped days / symbols ({result.skipped.length})</CardTitle>
          </CardHeader>
          <CardContent className="max-h-48 overflow-auto text-xs font-mono space-y-1">
            {result.skipped.slice(0, 50).map((r, i) => (
              <div key={i} className="text-muted-foreground">
                {r.ticker || r.symbol || "—"} {r.date ? `· ${r.date}` : ""} — {r.reason}
              </div>
            ))}
            {result.skipped.length > 50 && (
              <div className="text-xs">… and {result.skipped.length - 50} more.</div>
            )}
          </CardContent>
        </Card>
      )}
      {Array.isArray(result.earnings_warnings) && result.earnings_warnings.length > 0 && (
        <Alert variant="destructive" data-testid="orb-earnings-warning">
          <AlertTitle className="text-sm font-semibold">Earnings data warnings</AlertTitle>
          <AlertDescription className="text-xs space-y-1 mt-1">
            {result.earnings_warnings.map((w, i) => (
              <div key={i} className="font-mono" data-testid={`orb-earnings-warning-${i}`}>{w}</div>
            ))}
          </AlertDescription>
        </Alert>
      )}
      {result.earnings_loaded && Object.keys(result.earnings_loaded).length > 0 && (
        <Card data-testid="orb-earnings-loaded">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Earnings dates loaded</CardTitle>
            <CardDescription className="text-xs">
              Hour-aware blocking (TradingView parity): <span className="font-mono">bmo</span> blocks print day + next trading day, <span className="font-mono">amc</span> blocks next 2 trading days (print day allowed), <span className="font-mono">unknown</span> blocks print day + next 2 trading days. Source shown per symbol.
            </CardDescription>
          </CardHeader>
          <CardContent className="max-h-72 overflow-auto text-xs space-y-3">
            {Object.entries(result.earnings_loaded).map(([sym, info]) => {
              const events = (info && info.events) || [];
              const source = (info && info.source) || "unknown";
              const warnings = (info && info.warnings) || [];
              const empty = events.length === 0;
              return (
                <div key={sym} data-testid={`orb-earnings-loaded-${sym}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono font-semibold">{sym}</span>
                    <Badge
                      variant={empty ? "destructive" : (source === "finnhub" ? "default" : "secondary")}
                      className="text-[10px] uppercase"
                      data-testid={`orb-earnings-source-${sym}`}
                    >
                      source: {source}
                    </Badge>
                    {empty && (
                      <span className="text-[11px] text-destructive font-semibold">
                        NO EARNINGS LOADED — entries will not be blocked
                      </span>
                    )}
                  </div>
                  {warnings.length > 0 && (
                    <div className="pl-3 text-[11px] text-destructive/90 mb-1 space-y-0.5">
                      {warnings.map((w, i) => <div key={i} className="font-mono">{w}</div>)}
                    </div>
                  )}
                  {events.length > 0 ? (
                    <div className="pl-3 space-y-1 border-l border-border/60">
                      {events.map((ev, idx) => (
                        <div key={`${sym}-${idx}`} className="flex flex-wrap gap-x-3 gap-y-0.5">
                          <span className="font-mono">{ev.date}</span>
                          <Badge variant="outline" className="text-[10px] uppercase">
                            {ev.hour || "unknown"}
                          </Badge>
                          {ev.raw_hour && ev.raw_hour !== ev.hour && (
                            <span className="text-[10px] text-muted-foreground">raw: {ev.raw_hour}</span>
                          )}
                          <span className="text-muted-foreground">
                            blocks: <span className="font-mono">{(ev.blocked_dates || []).join(", ") || "(none)"}</span>
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="pl-3 text-muted-foreground">(no earnings in window)</div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function SetupResultsPanel({ data, setupNumber, onDownloadCsv }) {
  if (!data) return null;
  const s = data.summary || {};
  const trades = data.trades || [];
  const curve = data.equity_curve || [];
  const equityDelta = (s.final_equity ?? 0) - (curve[0]?.equity ?? 0);

  return (
    <div className="space-y-4">
      {/* Summary row */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <SummaryStat label="Trades" value={s.trades ?? 0} testId={`s${setupNumber}-trades`} />
        <SummaryStat label="Wins / Losses" value={`${s.wins ?? 0} / ${s.losses ?? 0}`} />
        <SummaryStat label="Win rate" value={`${r2(s.win_rate)}%`} />
        <SummaryStat label="Total R" value={r2(s.total_r)} valueClass={pnlClass(s.total_r)} />
        <SummaryStat label="Avg R / trade" value={r2(s.avg_r)} valueClass={pnlClass(s.avg_r)} />
        <SummaryStat label="Profit factor" value={s.profit_factor == null ? "∞" : r2(s.profit_factor)} />
        <SummaryStat label="Total P/L" value={money(s.total_pnl)} valueClass={pnlClass(s.total_pnl)} />
        <SummaryStat label="Max drawdown" value={`${r2(s.max_drawdown)}%`} valueClass="text-red-600 dark:text-red-400" />
        <SummaryStat
          label="Final equity"
          value={money(s.final_equity)}
          valueClass={pnlClass(equityDelta)}
          testId={`s${setupNumber}-final-equity`}
        />
      </div>

      {/* Equity curve */}
      {curve.length > 1 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Equity curve</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="w-full h-56" data-testid={`s${setupNumber}-equity-chart`}>
              <ResponsiveContainer>
                <LineChart data={curve} margin={{ top: 5, right: 10, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(v) => money(v)}
                    labelFormatter={(l) => `Day: ${l}`}
                  />
                  <Line type="monotone" dataKey="equity" stroke="#10b981" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trades table */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-sm">Trade log ({trades.length})</CardTitle>
          <Button size="sm" variant="outline" onClick={onDownloadCsv} data-testid={`s${setupNumber}-csv`}>
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[560px] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead>Stock</TableHead>
                  <TableHead>Entry date</TableHead>
                  <TableHead>Signal time (NY / UK)</TableHead>
                  <TableHead className="text-right">Entry</TableHead>
                  <TableHead className="text-right">Stop</TableHead>
                  <TableHead className="text-right">Target</TableHead>
                  <TableHead className="text-right">Shares</TableHead>
                  <TableHead>Exit date</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">P/L $</TableHead>
                  <TableHead className="text-right">R</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {trades.length === 0 && (
                  <TableRow><TableCell colSpan={11} className="text-center text-muted-foreground py-6">No trades.</TableCell></TableRow>
                )}
                {trades.map((t, i) => (
                  <TableRow key={i} data-testid={`s${setupNumber}-trade-${i}`}>
                    <TableCell className="font-mono">{t.symbol}</TableCell>
                    <TableCell className="font-mono">{t.entry_date}</TableCell>
                    <TableCell className="font-mono text-xs">
                      {t.entry_time_ny} NY / {t.entry_time_uk} UK
                      <span className="text-muted-foreground"> ({t.entry_bar_open_ny} open)</span>
                    </TableCell>
                    <TableCell className="font-mono text-right">{r2(t.entry)}</TableCell>
                    <TableCell className="font-mono text-right">{r2(t.stop)}</TableCell>
                    <TableCell className="font-mono text-right">{r2(t.target)}</TableCell>
                    <TableCell className="font-mono text-right">{t.shares}</TableCell>
                    <TableCell className="font-mono">
                      {t.exit_date}
                      <div className="text-xs text-muted-foreground">{t.exit_time_ny} NY</div>
                    </TableCell>
                    <TableCell>{exitBadge(t.exit_reason, t.pnl_dollars)}</TableCell>
                    <TableCell className={`font-mono text-right ${pnlClass(t.pnl_dollars)}`}>{money(t.pnl_dollars)}</TableCell>
                    <TableCell className={`font-mono text-right ${pnlClass(t.result_r)}`}>{r2(t.result_r)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function SummaryStat({ label, value, valueClass, testId }) {
  return (
    <div className="rounded-md border p-2" data-testid={testId}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold ${valueClass || ""}`}>{value}</div>
    </div>
  );
}

function ReplayPanel() {
  return (
    <div className="space-y-4">
      <RangeReplay />
      <SingleDayReplay />
    </div>
  );
}

/* ---------------------------- Live Trades & Jobs panel ---------------- */
const RUN_NOW_JOBS = [
  { key: "daily_refresh", label: "Daily refresh (09:00 NY)" },
  { key: "opening_range", label: "Opening range (10:00:30)" },
  { key: "signal_check", label: "Signal check (10:30–12:00)" },
  { key: "exit_check", label: "Exit check (every 30m)" },
  { key: "sip_recheck", label: "SIP recheck (16:30)" },
];

function LivePanel() {
  const [trades, setTrades] = useState([]);
  const [runs, setRuns] = useState([]);
  const [logStartDate, setLogStartDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});
  const [filter, setFilter] = useState("all");
  const [includeBackfill, setIncludeBackfill] = useState(false);
  const [leader, setLeader] = useState(null);

  const fetchAll = async () => {
    try {
      const qs = includeBackfill ? "?limit=1000&include_backfill=true" : "?limit=1000";
      const [t, j, l] = await Promise.all([
        apiGet(`/orb/trades${qs}`),
        apiGet("/orb/job-runs?limit=50"),
        apiGet("/orb/leader-status").catch(() => null),
      ]);
      setTrades(t.trades || []);
      setLogStartDate(t.log_start_date || null);
      setRuns(j.runs || []);
      setLeader(l);
    } catch (e) {
      toast.error("Could not load live data", { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); const i = setInterval(fetchAll, 30000); return () => clearInterval(i); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeBackfill]);

  const runNow = async (job) => {
    setBusy((b) => ({ ...b, [job]: true }));
    try {
      const r = await apiPost(`/orb/run-now/${job}`, {});
      toast.success(`${job} executed`, { description: JSON.stringify(r.result).slice(0, 180) });
      fetchAll();
    } catch (e) {
      toast.error(`${job} failed`, { description: e?.response?.data?.detail || e.message });
    } finally {
      setBusy((b) => ({ ...b, [job]: false }));
    }
  };

  const openTrades = trades.filter((t) => t.status === "open");
  const closedTrades = trades.filter((t) => t.status === "closed");
  const backfillCount = trades.filter((t) => t.source === "backfill").length;
  const shown = filter === "open" ? openTrades : filter === "closed" ? closedTrades : trades;

  if (loading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="space-y-4" data-testid="orb-live-panel">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 justify-between">
            <span className="flex items-center gap-2"><Play className="h-4 w-4" /> Run check now</span>
            <Button size="sm" variant="ghost" onClick={fetchAll} data-testid="orb-live-refresh">
              <RefreshCw className="h-4 w-4 mr-2" /> Refresh
            </Button>
          </CardTitle>
          <CardDescription className="space-y-1">
            <div>
              Manually trigger any Stage 2 job. Jobs are idempotent — safe to click multiple times.
              {logStartDate && (
                <span> Log start date: <span className="font-mono">{logStartDate}</span></span>
              )}
            </div>
            {leader && (
              <div className="text-xs flex items-center gap-2 flex-wrap">
                {leader.self_is_leader
                  ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800" data-testid="orb-leader-self">This worker is the scheduler leader</Badge>
                  : <Badge variant="outline" data-testid="orb-leader-follower">This worker is a follower</Badge>}
                <span className="font-mono text-[11px] text-muted-foreground">
                  leader={leader.leader_instance_id || "—"} · heartbeat {leader.leader_heartbeat_age_sec !== null && leader.leader_heartbeat_age_sec !== undefined ? Math.round(leader.leader_heartbeat_age_sec) + "s ago" : "—"}
                </span>
              </div>
            )}
            <div className="text-xs flex items-center gap-2 pt-1">
              <Switch
                id="orb-include-backfill"
                checked={includeBackfill}
                onCheckedChange={setIncludeBackfill}
                data-testid="orb-include-backfill-toggle"
              />
              <Label htmlFor="orb-include-backfill" className="cursor-pointer">
                Include backfill in stats &amp; charts
                <span className="text-muted-foreground ml-2">
                  (visual only — Telegram alerts never fire for backfilled trades)
                </span>
              </Label>
            </div>
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {RUN_NOW_JOBS.map((j) => (
            <Button
              key={j.key}
              variant="outline"
              size="sm"
              onClick={() => runNow(j.key)}
              disabled={!!busy[j.key]}
              data-testid={`orb-run-now-${j.key}`}
            >
              {busy[j.key] ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
              {j.label}
            </Button>
          ))}
        </CardContent>
      </Card>

      <Card data-testid="orb-live-trades">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 justify-between">
            <span>Live trades ({trades.length}{includeBackfill && backfillCount > 0 ? `, incl. ${backfillCount} backfill` : ""})</span>
            <div className="flex gap-1 text-xs">
              {["all", "open", "closed"].map((k) => (
                <Button
                  key={k}
                  size="sm"
                  variant={filter === k ? "default" : "outline"}
                  onClick={() => setFilter(k)}
                  data-testid={`orb-trades-filter-${k}`}
                >
                  {k === "all" ? `All ${trades.length}` : k === "open" ? `Open ${openTrades.length}` : `Closed ${closedTrades.length}`}
                </Button>
              ))}
            </div>
          </CardTitle>
          <CardDescription>
            Auto-populated by the signal and exit jobs. Each trade stores the settings in force at open.
            {includeBackfill
              ? <span className="ml-1">Backfill rows are shown here only because the toggle above is on; they never trigger Telegram alerts.</span>
              : null}
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[520px] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead>Stock</TableHead>
                  <TableHead>Setup</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Entry date</TableHead>
                  <TableHead className="text-right">Entry</TableHead>
                  <TableHead className="text-right">Stop</TableHead>
                  <TableHead className="text-right">Target</TableHead>
                  <TableHead className="text-right">Shares</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Exit</TableHead>
                  <TableHead className="text-right">P/L $</TableHead>
                  <TableHead className="text-right">R</TableHead>
                  <TableHead>Flags</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {shown.length === 0 && (
                  <TableRow><TableCell colSpan={13} className="text-center text-muted-foreground py-6">
                    No trades yet. The scheduler will add rows as signals fire.
                  </TableCell></TableRow>
                )}
                {shown.map((t) => (
                  <TableRow key={t.id} data-testid={`orb-trade-row-${t.id}`}>
                    <TableCell className="font-mono">{t.symbol}</TableCell>
                    <TableCell>S{t.setup}</TableCell>
                    <TableCell>
                      {t.source === "backfill"
                        ? <Badge variant="outline" className="text-amber-700 border-amber-300 dark:text-amber-300 dark:border-amber-700">backfill</Badge>
                        : <Badge variant="outline" className="text-sky-700 border-sky-300 dark:text-sky-300 dark:border-sky-700">live</Badge>}
                    </TableCell>
                    <TableCell className="font-mono">{t.entry_date}</TableCell>
                    <TableCell className="font-mono text-right">{r2(t.entry)}</TableCell>
                    <TableCell className="font-mono text-right">{r2(t.stop)}</TableCell>
                    <TableCell className="font-mono text-right">{r2(t.target)}</TableCell>
                    <TableCell className="font-mono text-right">{t.shares}</TableCell>
                    <TableCell>
                      {t.status === "open"
                        ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">Open</Badge>
                        : <Badge variant="outline">Closed</Badge>}
                    </TableCell>
                    <TableCell>
                      {t.exit_reason ? exitBadge(t.exit_reason, t.pnl_dollars) : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className={`font-mono text-right ${pnlClass(t.pnl_dollars)}`}>
                      {t.pnl_dollars !== null && t.pnl_dollars !== undefined ? money(t.pnl_dollars) : "—"}
                    </TableCell>
                    <TableCell className={`font-mono text-right ${pnlClass(t.result_r)}`}>
                      {t.result_r !== null && t.result_r !== undefined ? r2(t.result_r) : "—"}
                    </TableCell>
                    <TableCell className="text-xs">
                      {t.earnings_warning && <Badge variant="outline" className="mr-1">earnings</Badge>}
                      {t.fits_limits === false && <Badge variant="outline" className="mr-1">cap</Badge>}
                      {t.signal_confirmed === false && <Badge variant="destructive">Not confirmed</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="orb-job-runs">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2"><History className="h-4 w-4" /> Job log (last {runs.length})</CardTitle>
          <CardDescription>Every scheduled or manual job writes one line here.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="max-h-[380px] overflow-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-card">
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Result</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead className="text-right">ms</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.length === 0 && (
                  <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No job runs yet.</TableCell></TableRow>
                )}
                {runs.map((r) => (
                  <TableRow key={r.id} data-testid={`orb-job-run-${r.id}`}>
                    <TableCell className="text-xs font-mono">{fmtDateTime(r.at)}</TableCell>
                    <TableCell className="font-mono text-xs">{r.job}</TableCell>
                    <TableCell>
                      {r.result === "ok" && <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800">ok</Badge>}
                      {r.result === "skipped" && <Badge variant="outline">skipped</Badge>}
                      {r.result === "failed" && <Badge variant="destructive">failed</Badge>}
                    </TableCell>
                    <TableCell className="text-xs">{r.message || "—"}</TableCell>
                    <TableCell className="text-xs font-mono text-right">{r.duration_ms ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


/* ---------------------------- Backfill panel -------------------------- */
function BackfillPanel() {
  const [selected, setSelected] = useState([]);
  const [allWatchlist, setAllWatchlist] = useState(false);
  const [start, setStart] = useState(minusMonths(todayISO(), 3));
  const [end, setEnd] = useState(todayISO());
  const [jobId, setJobId] = useState(null);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const [rows, setRows] = useState([]);
  const [filterSymbol, setFilterSymbol] = useState("");
  const [filterSetup, setFilterSetup] = useState("");
  const [listLoading, setListLoading] = useState(true);

  const rangeDays = useMemo(() => diffDays(start, end), [start, end]);
  const rangeInvalid = rangeDays < 0 || rangeDays > 366;

  const fetchList = async () => {
    setListLoading(true);
    try {
      const p = new URLSearchParams({ limit: "1000" });
      if (filterSymbol.trim()) p.set("symbol", filterSymbol.trim().toUpperCase());
      if (filterSetup) p.set("setup", filterSetup);
      const r = await apiGet(`/orb/backfill-trades?${p.toString()}`);
      setRows(r.trades || []);
    } catch (e) {
      toast.error("Could not load backfill rows", { description: e?.response?.data?.detail || e.message });
    } finally {
      setListLoading(false);
    }
  };
  useEffect(() => { fetchList(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const start_ = async () => {
    setResult(null);
    setError(null);
    setStatus(null);
    try {
      const r = await apiPost("/orb/backfill-trades", {
        tickers: allWatchlist ? [] : selected,
        all_watchlist: allWatchlist,
        start,
        end,
      });
      setJobId(r.job_id);
    } catch (e) {
      toast.error("Could not start backfill", { description: e?.response?.data?.detail || e.message });
    }
  };

  useEffect(() => {
    if (!jobId) return;
    const tick = async () => {
      try {
        const r = await apiGet(`/orb/backfill-trades/jobs/${jobId}`);
        setStatus(r);
        if (r.status === "completed") {
          setResult(r.result || null);
          clearInterval(pollRef.current);
          pollRef.current = null;
          fetchList();
          toast.success("Backfill complete", {
            description: `${r.result?.inserted || 0} inserted · ${r.result?.replaced || 0} replaced · ${(r.result?.skipped_due_to_live || []).length} skipped (live row exists)`,
          });
        } else if (r.status === "failed") {
          setError(r.error || "backfill failed");
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
    tick();
    pollRef.current = setInterval(tick, 1500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps

  const progress = status?.progress || null;
  const pct = progress && progress.total ? Math.min(100, Math.round((progress.current / progress.total) * 100)) : 0;

  const exportCsv = () => {
    if (!rows.length) {
      toast.info("No backfill rows to export");
      return;
    }
    const header = [
      "symbol", "setup", "entry_date", "entry_time_ny", "entry", "stop", "target", "shares",
      "exit_date", "exit_time_ny", "exit", "exit_reason", "pnl_dollars", "result_r",
      "equity_before", "equity_after", "earnings_warning", "feed", "backfilled_at",
    ];
    const esc = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      return s.includes(",") || s.includes("\"") ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const body = rows.map((t) => header.map((k) => esc(t[k])).join(","));
    const csv = [header.join(","), ...body].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `oracle_backfill_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const deleteAll = async () => {
    const filterNote = filterSymbol ? ` for ${filterSymbol.toUpperCase()}` : "";
    if (!window.confirm(`Delete all backfill rows${filterNote}? Live trades are never touched.`)) return;
    try {
      const p = new URLSearchParams();
      if (filterSymbol.trim()) p.set("symbol", filterSymbol.trim().toUpperCase());
      const r = await apiDelete(`/orb/backfill-trades?${p.toString()}`);
      toast.success(`Deleted ${r.deleted || 0} backfill rows`);
      fetchList();
    } catch (e) {
      toast.error("Delete failed", { description: e?.response?.data?.detail || e.message });
    }
  };

  return (
    <div className="space-y-4" data-testid="orb-backfill-panel">
      <Alert className="border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800">
        <AlertTriangle className="h-4 w-4" />
        <AlertTitle>Backfill is historical only — Telegram never fires</AlertTitle>
        <AlertDescription className="text-sm">
          Backfilled trades are tagged <span className="font-mono">source="backfill"</span>, kept
          separate from live paper trades, and are excluded from live stats, open-position caps
          and alerts by default. The "Include backfill" toggle on the Live tab is visual only —
          Telegram never sends backfill alerts, even with the toggle on.
        </AlertDescription>
      </Alert>

      <Card data-testid="orb-backfill-run-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Clock className="h-4 w-4" /> Backfill historical trades</CardTitle>
          <CardDescription>
            Replays the engine across the chosen date range (Alpaca SIP feed) and writes each
            closed trade into <span className="font-mono">orb_trades</span> with
            <span className="font-mono"> source="backfill"</span>. Existing
            <span className="font-mono"> source="live"</span> rows are never overwritten.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <StockPicker
            selected={selected}
            onChange={setSelected}
            allWatchlist={allWatchlist}
            onAllWatchlistChange={setAllWatchlist}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="orb-bf-start">Start date</Label>
              <Input
                id="orb-bf-start"
                type="date"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                data-testid="orb-backfill-start"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="orb-bf-end">End date</Label>
              <Input
                id="orb-bf-end"
                type="date"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                data-testid="orb-backfill-end"
              />
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={start_}
              disabled={rangeInvalid || (!allWatchlist && !selected.length) || !!(jobId && !result && !error)}
              data-testid="orb-backfill-run"
            >
              <Play className="h-4 w-4 mr-2" /> Run backfill
            </Button>
            <span className="text-sm text-muted-foreground">
              {rangeDays >= 0 ? `${rangeDays + 1} day${rangeDays === 0 ? "" : "s"}` : "invalid range"}
              {rangeInvalid && <span className="text-destructive"> — must be ≤ 12 months</span>}
            </span>
          </div>

          {jobId && !result && !error && (
            <div className="space-y-2" data-testid="orb-backfill-progress">
              <Progress value={pct} />
              <div className="text-xs text-muted-foreground">
                {progress ? `${progress.current}/${progress.total} ${progress.message || ""}` : "starting…"}
              </div>
            </div>
          )}
          {error && (
            <Alert variant="destructive"><AlertTitle>Backfill failed</AlertTitle><AlertDescription>{error}</AlertDescription></Alert>
          )}
          {result && (
            <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1" data-testid="orb-backfill-result">
              <div>
                Inserted: <b>{result.inserted || 0}</b>
                {" · "}Replaced: <b>{result.replaced || 0}</b>
                {" · "}Skipped (live existed): <b>{(result.skipped_due_to_live || []).length}</b>
              </div>
              <div className="text-xs text-muted-foreground">
                Feed: <span className="font-mono">{result.feed}</span>
                {" · "}Range: <span className="font-mono">{result.range?.start} → {result.range?.end}</span>
              </div>
              {(result.skipped_due_to_live || []).length > 0 && (
                <details className="text-xs text-muted-foreground pt-1">
                  <summary className="cursor-pointer">Skipped rows (live wins)</summary>
                  <ul className="mt-1 list-disc list-inside">
                    {result.skipped_due_to_live.map((s, i) => (
                      <li key={i} className="font-mono">{s.symbol} S{s.setup} {s.entry_date}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="orb-backfill-list-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 justify-between">
            <span>Backfilled trades ({rows.length})</span>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={fetchList} data-testid="orb-backfill-refresh">
                <RefreshCw className="h-4 w-4 mr-2" /> Refresh
              </Button>
              <Button size="sm" variant="outline" onClick={exportCsv} data-testid="orb-backfill-csv">
                <Download className="h-4 w-4 mr-2" /> CSV
              </Button>
              <Button size="sm" variant="destructive" onClick={deleteAll} data-testid="orb-backfill-delete">
                <Trash2 className="h-4 w-4 mr-2" /> Delete {filterSymbol ? filterSymbol.toUpperCase() : "all"}
              </Button>
            </div>
          </CardTitle>
          <CardDescription>
            Historical rows only. These never affect live stats, caps or Telegram.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 p-4">
          <div className="flex flex-wrap gap-2 items-end">
            <div className="space-y-1">
              <Label htmlFor="orb-bf-filter-symbol" className="text-xs">Symbol</Label>
              <Input
                id="orb-bf-filter-symbol"
                className="w-32 font-mono"
                value={filterSymbol}
                onChange={(e) => setFilterSymbol(e.target.value)}
                placeholder="AMD"
                data-testid="orb-backfill-filter-symbol"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="orb-bf-filter-setup" className="text-xs">Setup</Label>
              <select
                id="orb-bf-filter-setup"
                className="h-9 border rounded-md px-2 bg-background text-sm"
                value={filterSetup}
                onChange={(e) => setFilterSetup(e.target.value)}
                data-testid="orb-backfill-filter-setup"
              >
                <option value="">All</option>
                <option value="5">Setup 5</option>
                <option value="6">Setup 6</option>
              </select>
            </div>
            <Button size="sm" variant="outline" onClick={fetchList} data-testid="orb-backfill-apply">
              <Search className="h-4 w-4 mr-2" /> Apply
            </Button>
          </div>

          <div className="max-h-[520px] overflow-auto border rounded-md">
            {listLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : (
              <Table>
                <TableHeader className="sticky top-0 bg-card">
                  <TableRow>
                    <TableHead>Stock</TableHead>
                    <TableHead>Setup</TableHead>
                    <TableHead>Entry date</TableHead>
                    <TableHead className="text-right">Entry</TableHead>
                    <TableHead className="text-right">Stop</TableHead>
                    <TableHead className="text-right">Target</TableHead>
                    <TableHead className="text-right">Shares</TableHead>
                    <TableHead>Exit date</TableHead>
                    <TableHead>Exit</TableHead>
                    <TableHead className="text-right">P/L $</TableHead>
                    <TableHead className="text-right">R</TableHead>
                    <TableHead>Flags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={12} className="text-center text-muted-foreground py-6">
                        No backfill rows yet. Use the form above to create some.
                      </TableCell>
                    </TableRow>
                  )}
                  {rows.map((t) => (
                    <TableRow key={t.id} data-testid={`orb-backfill-row-${t.id}`}>
                      <TableCell className="font-mono">{t.symbol}</TableCell>
                      <TableCell>S{t.setup}</TableCell>
                      <TableCell className="font-mono">{t.entry_date}</TableCell>
                      <TableCell className="font-mono text-right">{r2(t.entry)}</TableCell>
                      <TableCell className="font-mono text-right">{r2(t.stop)}</TableCell>
                      <TableCell className="font-mono text-right">{r2(t.target)}</TableCell>
                      <TableCell className="font-mono text-right">{t.shares}</TableCell>
                      <TableCell className="font-mono">{t.exit_date || "—"}</TableCell>
                      <TableCell>
                        {t.exit_reason ? exitBadge(t.exit_reason, t.pnl_dollars) : <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className={`font-mono text-right ${pnlClass(t.pnl_dollars)}`}>
                        {t.pnl_dollars !== null && t.pnl_dollars !== undefined ? money(t.pnl_dollars) : "—"}
                      </TableCell>
                      <TableCell className={`font-mono text-right ${pnlClass(t.result_r)}`}>
                        {t.result_r !== null && t.result_r !== undefined ? r2(t.result_r) : "—"}
                      </TableCell>
                      <TableCell className="text-xs">
                        {t.earnings_warning && <Badge variant="outline" className="mr-1">earnings</Badge>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

